import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

/**
 * Storefront page header — persistent across catalogue, product, cart and
 * checkout so navigation is consistent. Highlights the current section and
 * surfaces the customer's sign-in state.
 */
export default function StoreHeader() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const { user, loading, logout } = useCustomerAuth();

  const link = (to, label, testid) => {
    const active =
      to === "/" ? pathname === "/" : pathname.startsWith(to);
    return (
      <Link
        to={to}
        data-testid={testid}
        className={`text-sm hover:scale-110 transition-all duration-200 inline-block ${
          active ? "text-white font-medium" : "text-white/70 hover:text-white"
        }`}
      >
        {label}
      </Link>
    );
  };

  const initials = (user?.full_name || user?.email || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="relative border-b border-red-900/40 sticky top-0 z-40 text-white overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-black via-red-950 to-black" />
      <div className="pointer-events-none absolute -top-16 left-1/3 h-40 w-96 rounded-full bg-red-600/25 blur-3xl" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
        <Link to="/" className="font-semibold text-lg tracking-tight text-white transition-transform hover:scale-105 inline-block">
          FAST FORCE
          <span className="hidden sm:inline text-xs text-white/60 ml-2 font-normal">
            Auto Spare Parts
          </span>
        </Link>
        <nav className="flex items-center gap-4 sm:gap-6">
          {link("/", "Home", "nav-home")}
          {link("/catalog", "Catalogue", "nav-catalog")}
          {link("/track", "Track order", "nav-track")}
          {user && link("/my/orders", "My orders", "nav-my-orders")}
          {user && link("/my/enquiries", "My enquiries", "nav-my-enquiries")}
          {loading ? null : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full border border-white/25 pl-1.5 pr-3 py-1 hover:bg-white/10 transition-all hover:scale-105"
                  data-testid="nav-user-menu"
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt=""
                      className="w-6 h-6 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="w-6 h-6 rounded-full bg-red-600 text-white text-[10px] grid place-items-center">
                      {initials}
                    </span>
                  )}
                  <span className="text-xs hidden sm:inline max-w-[9rem] truncate">
                    {user.full_name?.split(" ")[0] || user.email}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground truncate">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="nav-user-track">
                  <Link to="/track">Track my orders</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    nav("/");
                  }}
                  data-testid="nav-user-signout"
                  className="text-destructive focus:text-destructive"
                >
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              asChild
              size="sm"
              variant="outline"
              data-testid="nav-signin"
              className="border-white/40 bg-transparent text-white hover:bg-white hover:text-black transition-all hover:scale-105"
            >
              <Link to="/customer/login">Sign in</Link>
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
}
